<template>
  <div v-if="isAIIntegrationEnabled" class="position-relative">
    <button
      data-test-id="lia-button"
      class="lia-button"
      :disabled="isDisabled"
      @click="onClick"
    >
      <spinner
        v-if="isProcessing"
        data-test-id="lia-loading"
        class="lia-button__loading"
        size="small"
        color-scheme="dark"
      />
      <img
        v-else
        class="lia-button__avatar"
        src="~dashboard/assets/images/lia-avatar.png"
        :alt="$t('INTEGRATION_SETTINGS.OPEN_AI.LIA.AVATAR_ALT')"
      />
      <span class="lia-button__label">{{ $t(activeCommand.label) }}</span>
    </button>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import OpenAPI from 'dashboard/api/integrations/openapi';
import Spinner from 'shared/components/Spinner';
import alertMixin from 'shared/mixins/alertMixin';
import { OPEN_AI_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

const LIA_COMMANDS = {
  reply_suggestion: 'INTEGRATION_SETTINGS.OPEN_AI.LIA.SUGGEST_REPLY',
  rephrase: 'INTEGRATION_SETTINGS.OPEN_AI.LIA.IMPROVE_REPLY',
  summarize: 'INTEGRATION_SETTINGS.OPEN_AI.LIA.SUMMARIZE',
};

export default {
  components: { Spinner },
  mixins: [alertMixin],
  props: {
    conversationId: {
      type: Number,
      default: 0,
    },
    message: {
      type: String,
      default: '',
    },
    isPrivateNote: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uiFlags: {
        rephrase: false,
        reply_suggestion: false,
        summarize: false,
      },
      activeTone: 'professional',
      tones: [
        {
          key: 'professional',
          value: this.$t(
            'INTEGRATION_SETTINGS.OPEN_AI.TONE.OPTIONS.PROFESSIONAL'
          ),
        },
        {
          key: 'friendly',
          value: this.$t('INTEGRATION_SETTINGS.OPEN_AI.TONE.OPTIONS.FRIENDLY'),
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      appIntegrations: 'integrations/getAppIntegrations',
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
    }),
    isAIIntegrationEnabled() {
      return this.appIntegrations.find(
        integration => integration.id === 'openai' && !!integration.hooks.length
      );
    },
    hookId() {
      return this.appIntegrations.find(
        integration => integration.id === 'openai' && !!integration.hooks.length
      ).hooks[0].id;
    },
    activeCommand() {
      let type = 'reply_suggestion';
      if (this.isPrivateNote) {
        type = 'summarize';
      } else if (this.message.trim()) {
        type = 'rephrase';
      }
      return { type, label: LIA_COMMANDS[type] };
    },
    isProcessing() {
      return Object.values(this.uiFlags).some(Boolean);
    },
    isDisabled() {
      return this.isProcessing || (this.isPrivateNote && !!this.message.trim());
    },
  },
  mounted() {
    if (!this.appIntegrations.length) {
      this.$store.dispatch('integrations/get');
    }
  },
  methods: {
    onClick() {
      if (this.isDisabled) return;
      this.processEvent(this.activeCommand.type);
    },
    async recordAnalytics({ type, tone }) {
      const event = OPEN_AI_EVENTS[type.toUpperCase()];
      if (event) {
        this.$track(event, {
          type,
          tone,
        });
      }
    },
    async processEvent(type = 'rephrase') {
      this.uiFlags[type] = true;
      const { locale } = this.getAccount(this.currentAccountId);
      try {
        const result = await OpenAPI.processEvent({
          hookId: this.hookId,
          type,
          content: this.message,
          tone: this.activeTone,
          conversationId: this.conversationId,
          locale,
        });
        const {
          data: { message: generatedMessage },
        } = result;
        this.$emit('replace-text', generatedMessage || this.message);
        this.recordAnalytics({ type, tone: this.activeTone });
      } catch (error) {
        this.showAlert(this.$t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR'));
      } finally {
        this.uiFlags[type] = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.lia-button {
  display: inline-flex;
  align-items: center;
  gap: var(--space-small);
  height: 3.1rem;
  padding: 0 var(--space-normal) 0 var(--space-smaller);
  margin-right: var(--space-small);
  background: var(--white);
  border: 2px solid #83b8fc;
  border-radius: 10rem;
  color: #0d2693;
  cursor: pointer;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-normal);
  white-space: nowrap;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}

.lia-button__avatar,
.lia-button__loading {
  flex-shrink: 0;
  width: 2.2rem;
  height: 2.2rem;
}

.lia-button__avatar {
  border-radius: 50%;
}

.lia-button__loading {
  position: relative;
  padding: 0;
}
</style>
